import { Component, Suspense, type ReactNode } from 'react';

import { lazyWithRetry } from '@/shared/lib/lazy-with-retry';

// Lazy, so a browser with `?x=staff-chat` off never loads the card or the dock.
const StaffChatDashboardCard = lazyWithRetry(
  () => import('./staff-chat-dashboard-card').then((m) => ({ default: m.StaffChatDashboardCard })),
  'staff-chat-card',
);
const StaffChatDock = lazyWithRetry(
  () => import('../dock/staff-chat-dock').then((m) => ({ default: m.StaffChatDock })),
  'staff-chat-dock',
);

/**
 * Catches a failure of a staff-chat piece alone (a chunk that will not load, a render error) and draws nothing in
 * its place, so the page around it keeps working. Nothing is logged: these pieces hold message previews.
 */
export class StaffChatCardBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * The Dashboard's Team card in its own Suspense and error boundary (SR-14): a slow or failing card never holds back
 * or hides the Dashboard content. The shell mounts it only with `?x=staff-chat`.
 */
export function StaffChatDashboardSlot() {
  return (
    <StaffChatCardBoundary>
      <Suspense fallback={null}>
        <StaffChatDashboardCard />
      </Suspense>
    </StaffChatCardBoundary>
  );
}

/** The Team dock, mounted once by the shell (`?x=staff-chat`), in its own Suspense and error boundary. */
export function StaffChatDockSlot() {
  return (
    <StaffChatCardBoundary>
      <Suspense fallback={null}>
        <StaffChatDock />
      </Suspense>
    </StaffChatCardBoundary>
  );
}
